import { ROUTES } from 'app/utils/constants/paths';
import useAnalytics from 'core/hooks/useAnalytics';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { EVENT_NAMES } from '../constants/eventNames';
import { SESSION_STATUS } from '../constants/sessionStatus';
import practiceService from '../services/practiceService';
import { isKcPracticeSession, mapSessionTypeToOrigin } from '../utils/challengeTypeUtils';
import { buildLoadedSessionScopeProps } from '../utils/practiceAnalyticsUtils';

import type { PracticeScopeSessionSource } from '../utils/practiceAnalyticsUtils';

/**
 * usePracticeExitFlow - Owns leaving the practice view from the header X (TANAI-1994).
 * A student-created, in-progress session gets a confirmation dialog offering two exits:
 * "discard" (PATCH the discard endpoint, then leave) or "save for later" (just leave,
 * the current behavior). Tutor sessions — and the session-complete "¡Hecho!" button —
 * skip the dialog and exit straight away via `exitPractice`.
 *
 * A volatile single-KC practice (TANAI-2087) also confirms before leaving, but with a single
 * "Salir" that discards it: the session cannot be resumed, so "save for later" makes no sense
 * for it. It is the only type that returns to "Mi progreso" (where it was launched from)
 * instead of "Today"; `exitPractice` owns that decision so every exit — the dialog, the
 * session-complete "¡Hecho!" and the already-discarded safety net — lands on the same route.
 * Such a practice is recognised from EITHER of the two signals the read DTO carries — its
 * `sessionType` and its resolved `originKc` (TANAI-2141), which is `null` for any other session
 * type — so a session read back without its type cannot silently turn the X into an unconfirmed
 * exit to "Today".
 *
 * The analytics payload, the discard guard and the route live here so the view stays
 * pure JSX. On a discard failure a student-created session keeps the dialog open so the student
 * can retry; a volatile KC practice leaves anyway (see `discardPractice`).
 */

type UsePracticeExitFlowParams = {
  resetPracticeSession: () => void;
  /**
   * Loaded session, read for its `originKc`: the second signal that recognises a volatile KC
   * practice and the source of the practised KC of the analytics scope properties. The fields the
   * flow branches on stay flat — `sessionId`, `sessionStatus`, `sessionType`, `subject` — so
   * callers keep passing what they already have.
   */
  sessionData?: PracticeScopeSessionSource | null;
  sessionId?: string;
  sessionStatus?: string;
  sessionType?: string;
  subject?: string;
};

type UsePracticeExitFlowReturn = {
  closeExitDialog: () => void;
  discardPractice: () => Promise<void>;
  exitPractice: () => void;
  isDiscarding: boolean;
  isExitDialogOpen: boolean;
  isKcPractice: boolean;
  requestExit: () => void;
  saveForLater: () => void;
};

function usePracticeExitFlow({
  resetPracticeSession,
  sessionData,
  sessionId,
  sessionStatus,
  sessionType,
  subject,
}: UsePracticeExitFlowParams): UsePracticeExitFlowReturn {
  const history = useHistory();
  const { trackEvent } = useAnalytics();
  const [isDiscarding, setIsDiscarding] = useState<boolean>(false);
  const [isExitDialogOpen, setIsExitDialogOpen] = useState<boolean>(false);

  // The session type is the primary signal; `originKc` is the dedicated one the read DTO resolves
  // for a KC practice and nulls for every other type, so the recognition survives a session read
  // back without its type.
  const isKcPractice = isKcPracticeSession(sessionType) || Boolean(sessionData?.originKc);

  // A volatile KC practice is launched from "Mi progreso" and cannot be reopened, so leaving it
  // goes back there; every other session type keeps returning to "Today".
  const returnRoute = isKcPractice ? ROUTES.MY_PROGRESS : ROUTES.TODAY;

  const exitPractice = useCallback(() => {
    resetPracticeSession();
    history.replace(returnRoute);
  }, [history, resetPracticeSession, returnRoute]);

  const requestExit = useCallback(() => {
    // Both a student-created session (delete / save for later) and a volatile KC practice
    // (single "Salir" that discards it) confirm before leaving. A tutor session is only paused
    // by leaving, so it exits straight away. `isKcPractice` is checked on its own because a KC
    // session maps to the "tutor" origin on purpose (see mapSessionTypeToOrigin).
    if (isKcPractice || mapSessionTypeToOrigin(sessionType) === 'student') {
      setIsExitDialogOpen(true);
      return;
    }
    exitPractice();
  }, [exitPractice, isKcPractice, sessionType]);

  const closeExitDialog = useCallback(() => {
    setIsExitDialogOpen(false);
  }, []);

  const discardPractice = useCallback(async () => {
    // The ui Dialog disables its footer while loading, but guard anyway so a second
    // trigger never fires a duplicate PATCH.
    if (isDiscarding) return;

    setIsDiscarding(true);

    try {
      await practiceService.discardSession(sessionId);
      // The scope properties are empty for every session but a KC practice, so this payload is
      // unchanged for the student-created / tutor flows. The practised KC comes from the session's
      // resolved `originKc`.
      trackEvent(EVENT_NAMES.ACTIVITY_SESSION_DISCARDED, {
        practice_session_id: sessionId,
        type: sessionType,
        subject,
        ...buildLoadedSessionScopeProps({ isKcPractice, sessionData }),
      });
      exitPractice();
    } catch {
      // The service already logged the failure through its error handler.
      // A volatile KC practice discards on a best-effort basis: its dialog offers a SINGLE
      // action, so keeping it open would trap the student in a view with no other way out. The
      // session is volatile — an orphan row is harmless and never surfaces in "Hoy" (its type is
      // not whitelisted there) — so leaving is strictly better than getting stuck.
      if (isKcPractice) {
        exitPractice();
        return;
      }

      // A student-created session keeps the dialog open ("save for later" is still there) so the
      // student can retry; do not reset or navigate.
      setIsDiscarding(false);
    }
  }, [exitPractice, isDiscarding, isKcPractice, sessionData, sessionId, sessionType, subject, trackEvent]);

  const saveForLater = useCallback(() => {
    trackEvent(EVENT_NAMES.ACTIVITY_SESSION_SAVED_FOR_LATER, {
      practice_session_id: sessionId,
      type: sessionType,
      subject,
    });
    exitPractice();
  }, [exitPractice, sessionId, sessionType, subject, trackEvent]);

  // Safety net: a deep link that somehow resolves to an already discarded session must
  // never be playable, so bail straight back to the section it came from.
  useEffect(() => {
    if (sessionStatus === SESSION_STATUS.DISCARDED) {
      exitPractice();
    }
  }, [exitPractice, sessionStatus]);

  return {
    closeExitDialog,
    discardPractice,
    exitPractice,
    isDiscarding,
    isExitDialogOpen,
    isKcPractice,
    requestExit,
    saveForLater,
  };
}

export default usePracticeExitFlow;
